'use client'

import React from 'react'
import {
  ArrowRight,
  Shield,
  Eye,
  CheckCircle,
  XCircle,
  Rocket,
  AlertTriangle,
  FileCheck,
  Bot,
  User,
  Lock,
  Search,
  FileText,
  Users,
  Settings,
  BarChart3,
} from 'lucide-react'

/**
 * Thread-Based Engineering Governance Diagrams
 * Visual components for the Thread-Based Engineering blog post
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

/**
 * Governance Pipeline Comparison Diagram
 * Shows checks after the fact versus gates before merge
 */
export function GovernancePipelineDiagram() {
  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          WHEN CHECKS RUN: AFTER THE FACT VS BEFORE MERGE
        </h3>
        <p className='text-sm text-muted-foreground'>
          When you catch vulnerabilities determines how much they cost
        </p>
      </div>

      <div className='flex flex-col items-center gap-6'>
        {/* Traditional Pipeline */}
        <div className='w-full'>
          <div className='flex items-center gap-2 mb-3'>
            <XCircle className='w-5 h-5 text-red-500' />
            <span className='text-red-500 font-semibold text-sm uppercase tracking-wide'>
              Traditional Approach
            </span>
          </div>
          <div className='p-4 rounded-xl bg-red-500/10 border border-red-500/30'>
            <div className='flex flex-wrap items-center justify-center gap-2'>
              {[
                { label: 'Generate', icon: Bot },
                { label: 'Review', icon: Eye },
                { label: 'Test', icon: FileCheck },
                { label: 'Scan', icon: Search, highlight: true },
                { label: 'Fix', icon: AlertTriangle, highlight: true },
                { label: 'Deploy', icon: Rocket },
              ].map((step, index, arr) => (
                <React.Fragment key={step.label}>
                  <div
                    className={`flex flex-col items-center p-2 rounded-lg ${step.highlight ? 'bg-red-500/20 border border-red-500/50' : 'bg-muted'}`}
                  >
                    <step.icon
                      className={`w-5 h-5 ${step.highlight ? 'text-red-500' : 'text-muted-foreground'}`}
                    />
                    <span className='text-[10px] text-foreground mt-1'>
                      {step.label}
                    </span>
                  </div>
                  {index < arr.length - 1 && (
                    <ArrowRight className='w-3 h-3 text-muted-foreground flex-shrink-0' />
                  )}
                </React.Fragment>
              ))}
            </div>
            <p className='text-center text-red-500 text-xs mt-3'>
              Vulnerabilities discovered late = expensive remediation cycles
            </p>
          </div>
        </div>

        {/* Thread-Based Pipeline */}
        <div className='w-full'>
          <div className='flex items-center gap-2 mb-3'>
            <CheckCircle className='w-5 h-5 text-green-500' />
            <span className='text-green-600 dark:text-green-400 font-semibold text-sm uppercase tracking-wide'>
              Thread-Based Approach
            </span>
          </div>
          <div className='p-4 rounded-xl bg-green-500/10 border border-green-500/30'>
            <div className='flex flex-wrap items-center justify-center gap-2'>
              {[
                { label: 'Generate', icon: Bot },
                { label: 'Automated Gates', icon: Shield, highlight: true },
                { label: 'Human Review', icon: User, highlight: true },
                { label: 'Deploy', icon: Rocket },
              ].map((step, index, arr) => (
                <React.Fragment key={step.label}>
                  <div
                    className={`flex flex-col items-center p-2 rounded-lg ${step.highlight ? 'bg-green-500/20 border border-green-500/50' : 'bg-muted'}`}
                  >
                    <step.icon
                      className={`w-5 h-5 ${step.highlight ? 'text-green-600 dark:text-green-400' : 'text-[#FDC304]'}`}
                    />
                    <span className='text-[10px] text-foreground mt-1'>
                      {step.label}
                    </span>
                  </div>
                  {index < arr.length - 1 && (
                    <ArrowRight className='w-3 h-3 text-muted-foreground flex-shrink-0' />
                  )}
                </React.Fragment>
              ))}
            </div>
            <p className='text-center text-green-600 dark:text-green-400 text-xs mt-3'>
              Checks run before merge, so problems surface before deployment
            </p>
          </div>
        </div>

        {/* Key difference callout */}
        <div className='px-4 py-3 rounded-xl bg-[#008dcc]/10 border border-[#008dcc]/30'>
          <p className='text-xs md:text-sm text-center text-foreground'>
            <span className='font-semibold text-[#008dcc]'>
              Key Difference:
            </span>{' '}
            Veracode found risky security flaws in 45% of AI code tests (2025),
            so the checks have to run{' '}
            <span className='text-green-600 dark:text-green-400 font-semibold'>
              before merge
            </span>
            , not after deployment
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Singapore MGF Compliance Checklist
 */
export function ComplianceChecklist() {
  const requirements = [
    {
      requirement: 'Assess and bound risks upfront',
      hiveImplementation: 'Architecture defines agent limits and permissions',
      icon: Shield,
    },
    {
      requirement: 'Clear allocation of responsibilities',
      hiveImplementation: 'Each agent has defined scope and accountability',
      icon: Users,
    },
    {
      requirement: 'Meaningful human oversight',
      hiveImplementation: 'Human approval at significant checkpoints',
      icon: Eye,
    },
    {
      requirement: 'Automated monitoring',
      hiveImplementation: 'Automated gates run on every change',
      icon: BarChart3,
    },
    {
      requirement: 'Adaptive governance',
      hiveImplementation: 'Autonomy expands only as checks prove reliable',
      icon: Settings,
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='flex items-center justify-center gap-2 mb-2'>
        <Lock className='w-5 h-5 text-[#008dcc]' />
        <span className='text-xl md:text-2xl font-bold text-foreground'>
          HOW TBE MAPS TO THE SINGAPORE MGF
        </span>
      </div>
      <p className='text-sm text-muted-foreground text-center mb-6'>
        Our own assessment, not an audit. Framework published January 2026.
      </p>

      <div className='space-y-3'>
        {requirements.map((req, index) => (
          <div
            key={req.requirement}
            className='flex items-start gap-3 bg-background rounded-xl p-3 border border-border/60'
          >
            <div className='flex-shrink-0'>
              <div className='w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center'>
                <CheckCircle className='w-4 h-4 text-green-600 dark:text-green-400' />
              </div>
            </div>
            <div className='flex-1'>
              <div className='flex items-start gap-2'>
                <req.icon className='w-4 h-4 mt-0.5 flex-shrink-0 text-[#008dcc]' />
                <div>
                  <h4 className='text-foreground font-medium text-sm'>
                    {index + 1}. {req.requirement}
                  </h4>
                  <p className='text-muted-foreground text-xs mt-0.5'>
                    <span className='text-[#FDC304] font-medium'>TBE:</span>{' '}
                    {req.hiveImplementation}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Additional frameworks */}
      <div className='mt-4 grid grid-cols-1 md:grid-cols-2 gap-3'>
        <div className='bg-background rounded-xl p-3 border border-border/60'>
          <span className='text-foreground font-semibold text-sm mb-2 flex items-center gap-2'>
            <FileText className='w-4 h-4 text-[#8b5cf6]' />
            WEF Framework (Nov 2025)
          </span>
          <ul className='text-xs text-muted-foreground space-y-1'>
            <li className='flex items-center gap-1'>
              <CheckCircle className='w-3 h-3 text-green-500' />
              Pre-deployment testing
            </li>
            <li className='flex items-center gap-1'>
              <CheckCircle className='w-3 h-3 text-green-500' />
              Clear task boundaries
            </li>
            <li className='flex items-center gap-1'>
              <CheckCircle className='w-3 h-3 text-green-500' />
              Input/output filters
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}

/**
 * Metrics Comparison Diagram
 */
export function MetricsComparisonDiagram() {
  const metrics = [
    {
      metric: 'Security Vulnerabilities',
      industry: '45%',
      industryLabel: 'of AI code tests',
      tbe: '<5%',
      tbeLabel: 'of AI code tests',
      improvement: 'Our target',
      icon: Shield,
    },
    {
      metric: 'XSS Failure Rate',
      industry: '86%',
      industryLabel: 'failure',
      tbe: '<15%',
      tbeLabel: 'failure',
      improvement: 'Our target',
      icon: Lock,
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          INDUSTRY VS THREAD-BASED ENGINEERING
        </h3>
        <p className='text-sm text-muted-foreground'>
          Measurable outcomes from governance-first architecture
        </p>
      </div>

      <div className='space-y-3'>
        {metrics.map(m => (
          <div
            key={m.metric}
            className='bg-background rounded-xl p-4 border border-border/60'
          >
            <div className='flex items-center gap-2 mb-3'>
              <m.icon className='w-4 h-4 text-[#008dcc]' />
              <span className='text-foreground font-semibold text-sm'>
                {m.metric}
              </span>
            </div>

            <div className='grid grid-cols-3 gap-3'>
              {/* Industry */}
              <div className='text-center'>
                <div className='text-[10px] text-muted-foreground uppercase mb-1'>
                  Industry
                </div>
                <div className='text-xl font-bold text-red-500'>
                  {m.industry}
                </div>
                <div className='text-[10px] text-muted-foreground'>
                  {m.industryLabel}
                </div>
              </div>

              {/* Arrow */}
              <div className='flex items-center justify-center'>
                <div className='px-2 py-1 rounded-full text-[10px] font-semibold bg-green-500/20 text-green-600 dark:text-green-400'>
                  {m.improvement}
                </div>
              </div>

              {/* TBE */}
              <div className='text-center'>
                <div className='text-[10px] text-muted-foreground uppercase mb-1'>
                  TBE Target
                </div>
                <div className='text-xl font-bold text-green-600 dark:text-green-400'>
                  {m.tbe}
                </div>
                <div className='text-[10px] text-muted-foreground'>
                  {m.tbeLabel}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Source attribution */}
      <p className='text-center text-muted-foreground text-[10px] mt-4'>
        Sources: Veracode 2025 GenAI Code Security Report and AI-generated code
        security research. TBE values are our targets, not measured results.
      </p>
    </div>
  )
}
